/**
 * Quick Purchase + Confirm UI (Task 7).
 * Home screen: merchant + amount fast path, optional Item(s)/description.
 * Conditional prompts only. Confirm: exact total, date, card actually used
 * (defaults to recommended, overridable).
 */

import { useState } from 'react';
import type { Cents } from '../../domain/money';
import { asCents } from '../../domain/money';
import type { BusinessDate } from '../../domain/time';
import { businessDate } from '../../domain/time';
import { neededConditionalPrompts } from '../../domain/recommend/conditionalPrompts';
import { rankCards } from '../../domain/recommend/rankCards';
import type {
  PriorityMode,
  PurchaseQueryInput,
  RankCardsInput,
  RecommendationOutput,
} from '../../domain/recommend/types';

/** Civil UTC today for the confirm default — Date stays inside UI, not domain. */
function civilTodayUtc(): BusinessDate {
  const d = new Date();
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return businessDate(`${String(y)}-${m}-${day}`);
}

export type QuickPurchaseCardOption = {
  card_id: string;
  label: string;
};

export type QuickPurchaseConfirmArgs = {
  merchant_input: string;
  /** Optional Item(s) text; blank input becomes null. */
  description: string | null;
  amount_cents: Cents;
  exact_total_cents: Cents;
  transaction_date: BusinessDate;
  card_id_used: string;
  checking_account_label: string;
  rank_input: RankCardsInput;
  recommendation: RecommendationOutput;
};

export type QuickPurchaseScreenProps = {
  /** Cards available for the confirm override dropdown. */
  cards: readonly QuickPurchaseCardOption[];
  /**
   * Build a RankCardsInput for the current query fields.
   * Caller supplies portfolio snapshot; recommendation path never awaits network.
   */
  buildRankInput: (query: PurchaseQueryInput) => RankCardsInput;
  /** protection_prompt_threshold_cents from settings (default $300). */
  protectionPromptThresholdCents: Cents;
  /** Persist confirm — must write local Dexie + outbox without network. */
  onConfirm: (args: QuickPurchaseConfirmArgs) => Promise<void>;
  /** Optional: override "today" for tests. */
  today?: BusinessDate;
};

type Step = 'query' | 'prompts' | 'result' | 'confirm' | 'done';

function parseAmountToCents(raw: string): Cents | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(trimmed);
  if (match === null) return null;
  const dollars = BigInt(match[1] ?? '0');
  const frac = match[2] ?? '';
  const centsPart = frac.length === 0 ? 0n : frac.length === 1 ? BigInt(frac) * 10n : BigInt(frac);
  const total = dollars * 100n + centsPart;
  if (total <= 0n) return null;
  return asCents(total);
}

function formatCents(cents: Cents): string {
  const asBig = BigInt(cents);
  const neg = asBig < 0n;
  const abs = neg ? -asBig : asBig;
  const whole = abs / 100n;
  const frac = abs % 100n;
  const s = `${whole.toString()}.${frac.toString().padStart(2, '0')}`;
  return neg ? `−$${s}` : `$${s}`;
}

function baseQuery(
  merchant: string,
  amount: Cents,
  over: Partial<PurchaseQueryInput> = {},
): PurchaseQueryInput {
  return {
    merchant_input: merchant,
    merchant_id: null,
    category: null,
    amount_cents: amount,
    channel: 'unknown',
    geography: 'domestic',
    recurring: false,
    owner: 'personal',
    financing_requested: false,
    return_risk: false,
    mode: 'maximize_rewards',
    pay_before_statement_close: null,
    allow_interest_bearing: false,
    ...over,
  };
}

export function QuickPurchaseScreen({
  cards,
  buildRankInput,
  protectionPromptThresholdCents,
  onConfirm,
  today: todayProp,
}: QuickPurchaseScreenProps): JSX.Element {
  const asOf = todayProp ?? civilTodayUtc();

  const [step, setStep] = useState<Step>('query');
  const [merchant, setMerchant] = useState('');
  const [amountText, setAmountText] = useState('');
  const [itemsText, setItemsText] = useState('');
  const [payBefore, setPayBefore] = useState<boolean | null>(null);
  const [mode, setMode] = useState<PriorityMode>('maximize_rewards');
  const [needPayBefore, setNeedPayBefore] = useState(false);
  const [needProtection, setNeedProtection] = useState(false);
  const [recommendation, setRecommendation] = useState<RecommendationOutput | null>(null);
  const [rankInput, setRankInput] = useState<RankCardsInput | null>(null);
  const [exactTotalText, setExactTotalText] = useState('');
  const [txnDate, setTxnDate] = useState(asOf);
  const [cardUsed, setCardUsed] = useState('');
  const [checkingLabel, setCheckingLabel] = useState('primary');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function runRank(query: PurchaseQueryInput): void {
    const input = buildRankInput(query);
    const out = rankCards(input);
    setRankInput(input);
    setRecommendation(out);
    const winner = out.winner_card_id ?? out.eligible[0]?.card_id ?? cards[0]?.card_id ?? '';
    setCardUsed(winner);
    setExactTotalText(formatCents(query.amount_cents).replace(/^\$/, ''));
    setTxnDate(asOf);
    setStep('result');
  }

  function onSubmitQuery(e: React.FormEvent): void {
    e.preventDefault();
    setError(null);
    const amount = parseAmountToCents(amountText);
    if (merchant.trim() === '' || amount === null) {
      setError('Enter a merchant and a positive amount.');
      return;
    }
    const probe = baseQuery(merchant, amount);
    const prompts = neededConditionalPrompts(buildRankInput(probe), protectionPromptThresholdCents);
    setNeedPayBefore(prompts.pay_before_statement_close);
    setNeedProtection(prompts.protection_mode);
    setPayBefore(null);
    setMode('maximize_rewards');
    if (prompts.pay_before_statement_close || prompts.protection_mode) {
      setStep('prompts');
      return;
    }
    runRank(probe);
  }

  function onSubmitPrompts(e: React.FormEvent): void {
    e.preventDefault();
    setError(null);
    const amount = parseAmountToCents(amountText);
    if (amount === null) {
      setError('Amount is invalid.');
      return;
    }
    if (needPayBefore && payBefore === null) {
      setError('Say whether you will pay before statement close.');
      return;
    }
    runRank(
      baseQuery(merchant, amount, {
        pay_before_statement_close: needPayBefore ? payBefore : null,
        mode,
      }),
    );
  }

  async function onSubmitConfirm(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (recommendation === null || rankInput === null) return;
    const exact = parseAmountToCents(exactTotalText);
    if (exact === null) {
      setError('Enter the exact total.');
      return;
    }
    if (cardUsed === '') {
      setError('Choose the card actually used.');
      return;
    }
    let date: BusinessDate;
    try {
      date = businessDate(txnDate);
    } catch {
      setError('Transaction date must be YYYY-MM-DD.');
      return;
    }
    setBusy(true);
    try {
      const itemsTrimmed = itemsText.trim();
      await onConfirm({
        merchant_input: merchant.trim(),
        description: itemsTrimmed === '' ? null : itemsTrimmed,
        amount_cents: rankInput.query.amount_cents,
        exact_total_cents: exact,
        transaction_date: date,
        card_id_used: cardUsed,
        checking_account_label: checkingLabel.trim() === '' ? 'primary' : checkingLabel.trim(),
        rank_input: rankInput,
        recommendation,
      });
      setStep('done');
    } catch {
      setError('Could not save the purchase locally.');
    } finally {
      setBusy(false);
    }
  }

  function reset(): void {
    setStep('query');
    setMerchant('');
    setAmountText('');
    setItemsText('');
    setRecommendation(null);
    setRankInput(null);
    setError(null);
  }

  const winnerLabel =
    recommendation?.winner_card_id !== undefined && recommendation.winner_card_id !== null
      ? (cards.find((c) => c.card_id === recommendation.winner_card_id)?.label ??
        recommendation.winner_card_id)
      : null;

  return (
    <section data-testid="quick-purchase-screen" className="flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-100">Quick Purchase</h2>
        <p className="mt-1 text-sm text-slate-400">
          Merchant and amount — best card, instantly, offline. Item(s) optional.
        </p>
      </div>

      {error !== null ? (
        <p role="alert" className="text-sm text-rose-400">
          {error}
        </p>
      ) : null}

      {step === 'query' ? (
        <form
          data-testid="quick-purchase-query"
          onSubmit={onSubmitQuery}
          className="flex flex-col gap-3"
        >
          <label className="flex flex-col gap-1 text-sm text-slate-300">
            Merchant
            <input
              data-testid="merchant-input"
              className="rounded border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
              value={merchant}
              onChange={(ev) => setMerchant(ev.target.value)}
              autoComplete="off"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-slate-300">
            Amount (USD)
            <input
              data-testid="amount-input"
              inputMode="decimal"
              className="rounded border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
              value={amountText}
              onChange={(ev) => setAmountText(ev.target.value)}
              placeholder="0.00"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-slate-300">
            Item(s) <span className="font-normal text-slate-500">(optional)</span>
            <input
              data-testid="items-input"
              className="rounded border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
              value={itemsText}
              onChange={(ev) => setItemsText(ev.target.value)}
              autoComplete="off"
              placeholder="e.g. dog food"
            />
          </label>
          <button
            type="submit"
            data-testid="recommend-submit"
            className="rounded bg-sky-700 px-3 py-2 text-sm font-medium text-white hover:bg-sky-600"
          >
            Recommend
          </button>
        </form>
      ) : null}

      {step === 'prompts' ? (
        <form
          data-testid="quick-purchase-prompts"
          onSubmit={onSubmitPrompts}
          className="flex flex-col gap-3"
        >
          {needPayBefore ? (
            <fieldset className="flex flex-col gap-2 text-sm text-slate-300">
              <legend>Will you pay this card before statement close?</legend>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="pay-before"
                  data-testid="pay-before-yes"
                  checked={payBefore === true}
                  onChange={() => setPayBefore(true)}
                />
                Yes — pay before close
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="pay-before"
                  data-testid="pay-before-no"
                  checked={payBefore === false}
                  onChange={() => setPayBefore(false)}
                />
                No
              </label>
            </fieldset>
          ) : null}
          {needProtection ? (
            <fieldset className="flex flex-col gap-2 text-sm text-slate-300">
              <legend>Large purchase — prioritize protection?</legend>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="prot-mode"
                  data-testid="protection-yes"
                  checked={mode === 'maximize_protection'}
                  onChange={() => setMode('maximize_protection')}
                />
                Maximize protection
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="prot-mode"
                  data-testid="protection-no"
                  checked={mode === 'maximize_rewards'}
                  onChange={() => setMode('maximize_rewards')}
                />
                Maximize rewards
              </label>
            </fieldset>
          ) : null}
          <button
            type="submit"
            data-testid="prompts-continue"
            className="rounded bg-sky-700 px-3 py-2 text-sm font-medium text-white hover:bg-sky-600"
          >
            Continue
          </button>
        </form>
      ) : null}

      {step === 'result' && recommendation !== null ? (
        <div data-testid="quick-purchase-result" className="flex flex-col gap-3">
          {recommendation.no_eligible_card || recommendation.winner_card_id === null ? (
            <p className="rounded-lg border border-amber-900/60 bg-amber-950/40 p-4 text-sm text-amber-100">
              No eligible card for this purchase.
            </p>
          ) : (
            <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">Recommended</p>
              <p data-testid="recommended-card" className="mt-1 text-lg font-medium text-slate-100">
                {winnerLabel}
              </p>
              <ul className="mt-3 list-inside list-disc text-sm text-slate-300">
                {(
                  recommendation.eligible.find((c) => c.card_id === recommendation.winner_card_id)
                    ?.explanation ?? []
                ).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          )}
          {recommendation.warnings.length > 0 ? (
            <ul data-testid="result-warnings" className="text-sm text-amber-200/90">
              {recommendation.warnings.map((w) => (
                <li key={w.code + w.message}>{w.message}</li>
              ))}
            </ul>
          ) : null}
          {recommendation.ineligible.length > 0 ? (
            <details className="text-sm text-slate-400">
              <summary>Not currently eligible ({recommendation.ineligible.length})</summary>
              <ul className="mt-2 list-inside list-disc">
                {recommendation.ineligible.map((c) => (
                  <li key={c.card_id}>
                    {cards.find((x) => x.card_id === c.card_id)?.label ?? c.card_id}:{' '}
                    {c.gate_failures.map((g) => g.code).join(', ')}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="go-confirm"
              disabled={recommendation.winner_card_id === null && cards.length === 0}
              onClick={() => setStep('confirm')}
              className="rounded bg-sky-700 px-3 py-2 text-sm font-medium text-white hover:bg-sky-600 disabled:opacity-40"
            >
              Confirm purchase
            </button>
            <button
              type="button"
              data-testid="result-back"
              onClick={reset}
              className="rounded border border-slate-700 px-3 py-2 text-sm text-slate-300"
            >
              Start over
            </button>
          </div>
        </div>
      ) : null}

      {step === 'confirm' && recommendation !== null ? (
        <form
          data-testid="quick-purchase-confirm"
          onSubmit={(ev) => {
            void onSubmitConfirm(ev);
          }}
          className="flex flex-col gap-3"
        >
          <label className="flex flex-col gap-1 text-sm text-slate-300">
            Exact total (USD)
            <input
              data-testid="exact-total-input"
              inputMode="decimal"
              className="rounded border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
              value={exactTotalText}
              onChange={(ev) => setExactTotalText(ev.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-slate-300">
            Transaction date
            <input
              data-testid="txn-date-input"
              type="date"
              className="rounded border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
              value={txnDate}
              onChange={(ev) => setTxnDate(ev.target.value as BusinessDate)}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-slate-300">
            Card actually used
            <select
              data-testid="card-used-select"
              className="rounded border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
              value={cardUsed}
              onChange={(ev) => setCardUsed(ev.target.value)}
            >
              {cards.map((c) => (
                <option key={c.card_id} value={c.card_id}>
                  {c.label}
                  {c.card_id === recommendation.winner_card_id ? ' (recommended)' : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm text-slate-300">
            Checking account label
            <input
              data-testid="checking-label-input"
              className="rounded border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
              value={checkingLabel}
              onChange={(ev) => setCheckingLabel(ev.target.value)}
            />
          </label>
          <button
            type="submit"
            data-testid="confirm-submit"
            disabled={busy}
            className="rounded bg-emerald-700 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-40"
          >
            {busy ? 'Saving…' : 'Confirm'}
          </button>
        </form>
      ) : null}

      {step === 'done' ? (
        <div data-testid="quick-purchase-done" className="flex flex-col gap-3">
          <p className="text-sm text-emerald-300">Purchase recorded locally. Outbox queued.</p>
          <button
            type="button"
            data-testid="done-reset"
            onClick={reset}
            className="rounded border border-slate-700 px-3 py-2 text-sm text-slate-300"
          >
            New purchase
          </button>
        </div>
      ) : null}
    </section>
  );
}
